import React, { useState, useEffect, useRef } from "react";
import {
  HardHat,
  Briefcase,
  ShieldAlert,
  Wrench,
  ClipboardCheck,
  FileText,
  Activity,
  Archive,
  BarChart3,
  Bell,
  GraduationCap,
  CheckCircle2,
  ArrowRight,
  Users,
  Play,
  Pause,
} from "lucide-react";
import { useT } from "../lib/i18n";

/**
 * WorkflowChainPanel
 * --------------------------------------------------
 * Shows the operational routing magic — a single submission cascading
 * through every part of the system. Three real workflow chains:
 *
 *   1. Daily Report submitted → PM notified → PDF generated → KPI updated → Archive
 *   2. Equipment Pre-Op FAILS → Shop notified → Unit locked out → Repair → RTS log
 *   3. Incident submitted → Safety alerted → Corrective action → Training assigned → Exec visibility
 *
 * Restrained design: simple connected nodes with directional arrows.
 * Tabbed selector. NO arcade animation, NO chase lights. The accent
 * line that flows once when you switch tabs is a single subtle pulse —
 * mission-critical, not gamified.
 */

const CHAINS = [
  {
    id: "daily-report",
    title: "Daily Report submitted",
    eyebrow: "Field → Office · Routing",
    summary:
      "Crew submits at the trailer. Within 60 seconds the PM has it, a branded PDF is rendered, KPIs roll forward, and the record is archived for audit.",
    nodes: [
      { icon: HardHat,        actor: "Field Crew",        action: "Daily Report submitted",       meta: "DR-5511 · JOB 2098",       tone: "field" },
      { icon: Bell,           actor: "PM Console",        action: "Real-time notification",        meta: "Routed · 7s latency",       tone: "info" },
      { icon: FileText,       actor: "PDF Render",        action: "Branded PDF generated",          meta: "ForgedOps cover · auto",    tone: "info" },
      { icon: BarChart3,      actor: "Executive KPIs",    action: "Production · Hours · Cost",      meta: "Live roll-forward",         tone: "ok" },
      { icon: Archive,        actor: "Audit Archive",     action: "Immutable record stored",        meta: "30d soft-retention",         tone: "ok" },
    ],
  },
  {
    id: "preop-fail",
    title: "Equipment Pre-Op FAILS",
    eyebrow: "Field → Shop · Lockout",
    summary:
      "Operator flags a defect. The unit is locked out before the next shift, the shop sees it on their screen, repair is initiated, and Return-to-Service is logged with photos.",
    nodes: [
      { icon: HardHat,        actor: "Operator",          action: "Pre-Op fail recorded",           meta: "CAT 336 · UNIT 1142",       tone: "warn" },
      { icon: ShieldAlert,    actor: "System",            action: "Unit locked out",                meta: "Cannot dispatch",            tone: "critical" },
      { icon: Wrench,         actor: "Shop",              action: "Repair ticket opened",           meta: "SHOP-A · Mechanic 03",       tone: "info" },
      { icon: ClipboardCheck, actor: "Shop",              action: "Return-to-Service signed",       meta: "Photos · RTS-time",         tone: "ok" },
      { icon: Archive,        actor: "Fleet Log",         action: "Downtime + cost captured",       meta: "Auto-tagged to job",        tone: "ok" },
    ],
  },
  {
    id: "incident",
    title: "Incident reported",
    eyebrow: "Field → Safety → Exec · Escalation",
    summary:
      "Incident submitted from the field. Safety lead is paged, a corrective action is generated, training is auto-assigned to the affected crew, and the incident becomes visible at executive level — same day.",
    nodes: [
      { icon: ShieldAlert,    actor: "Field Crew",        action: "Incident submitted",             meta: "INC-2042 · Trench Ops",     tone: "critical" },
      { icon: Bell,           actor: "Safety Lead",       action: "Paged · stop-the-line check",   meta: "SMS + push · 12s",           tone: "warn" },
      { icon: ClipboardCheck, actor: "Corrective Action", action: "Auto-generated · owner assigned", meta: "Due 24h",                    tone: "info" },
      { icon: GraduationCap,  actor: "Training Center",   action: "Training auto-assigned",          meta: "Crew · 4 ops · EN+ES",       tone: "info" },
      { icon: BarChart3,      actor: "Executive Dash",    action: "Same-day visibility",             meta: "OSHA-ready trail",           tone: "ok" },
    ],
  },
  {
    id: "weather-hold",
    title: "Weather hold called",
    eyebrow: "Site → Schedule · Production impact",
    summary:
      "Superintendent calls a weather hold. Production clock pauses, the schedule absorbs the impact, manpower is reallocated for the resume window, and the executive cost roll-up reflects the variance — all without an email chain.",
    nodes: [
      { icon: HardHat,        actor: "Superintendent",    action: "Weather hold called",             meta: "Wind 31mph · 14:07",         tone: "warn"     },
      { icon: Bell,           actor: "Crews on shift",    action: "Push notice · resume target",     meta: "Resume 14:45 · 38m hold",    tone: "info"     },
      { icon: ClipboardCheck, actor: "Production",        action: "Hours auto-paused · payroll",     meta: "48 ops · 38m absorbed",      tone: "info"     },
      { icon: Briefcase,      actor: "PM Console",        action: "Schedule variance flagged",       meta: "Job 2104 · -0.8 day",        tone: "warn"     },
      { icon: BarChart3,      actor: "Exec cost roll-up", action: "Variance posted · same-day",      meta: "Daily Report auto-tagged",   tone: "ok"       },
    ],
  },
  {
    id: "onboarding",
    title: "New crew onboarding",
    eyebrow: "Hiring → Field · Workforce deployment",
    summary:
      "A new operator is added. Onboarding pack auto-issued in their language, QR poster scanned at the trailer, training auto-assigned by role, and the operator's profile is live in the field within hours — not days.",
    nodes: [
      { icon: Briefcase,      actor: "HR / Admin",        action: "Operator added",                  meta: "OP-4471 · Spanish-first",    tone: "info"     },
      { icon: GraduationCap,  actor: "Training Center",   action: "Pack auto-issued · EN+ES",         meta: "Field readiness track",      tone: "info"     },
      { icon: Users,          actor: "Field Trailer",     action: "QR poster scanned · acks logged", meta: "JOB 2098 · 09:14",           tone: "ok"       },
      { icon: ClipboardCheck, actor: "Profile",           action: "Certs · acks · contacts live",     meta: "Visible to PM + super",      tone: "ok"       },
      { icon: BarChart3,      actor: "Workforce KPI",     action: "Readiness % updated",              meta: "Crew B-2 · 94%",             tone: "ok"       },
    ],
  },
  {
    id: "toolbox-talk",
    title: "Toolbox Talk · QR sign-in",
    eyebrow: "Field → Safety · Compliance",
    summary:
      "Crew scans the trailer QR. Attendance is captured per operator, the safety acknowledgment routes, the compliance dashboard refreshes, an OSHA-ready PDF is rendered, and every signature lands in the operator file.",
    nodes: [
      { icon: Users,          actor: "Field Crew",        action: "Toolbox Talk · QR sign-in",        meta: "Crew · 7 ops · EN+ES",       tone: "field"    },
      { icon: ShieldAlert,    actor: "Safety Lead",       action: "Acknowledgment routed",            meta: "Hazards reviewed · auto",     tone: "warn"     },
      { icon: ClipboardCheck, actor: "Compliance",        action: "Crew compliance updated",          meta: "Acks · attendance logged",   tone: "info"     },
      { icon: FileText,       actor: "PDF Render",        action: "OSHA-ready packet generated",      meta: "Branded · auto-stored",      tone: "info"     },
      { icon: Archive,        actor: "Operator Files",    action: "Per-op signature archived",        meta: "30d retention · audit",      tone: "ok"       },
    ],
  },
];

const TONE = {
  field:    { color: "#FF6A00" },
  info:     { color: "#FFFFFF" },
  warn:     { color: "#FFB000" },
  critical: { color: "#FF6A6A" },
  ok:       { color: "#65DA76" },
};

/**
 * Maps incoming submission `kind` values (from window 'forgeops:submission'
 * events) to a chain id in CHAINS. Keep the keys lowercase + kebab.
 */
const SUBMISSION_TO_CHAIN = {
  "toolbox-talk":   "toolbox-talk",
  "daily-report":   "daily-report",
  "incident":       "incident",
  "near-miss":      "incident",
  "preop-fail":     "preop-fail",
  "weather-hold":   "weather-hold",
  "onboarding":     "onboarding",
};

// Sequential-illumination pacing for live triggers. Each node takes
// LIVE_NODE_MS to "land". Restrained — each step must look intentional,
// not arcade.
const LIVE_NODE_MS = 850;

export default function WorkflowChainPanel() {
  const { t } = useT();
  const [activeId, setActiveId] = useState(CHAINS[0].id);
  const [isAutoTour, setIsAutoTour] = useState(true);
  const [isPaused, setIsPaused] = useState(false);     // pause-on-hover
  const [tickKey, setTickKey] = useState(0);            // restarts the progress bar animation

  // Live-trigger state. When a real demo submission fires the
  // `forgeops:submission` window event, the panel jumps to the matching
  // chain, marks itself "live", and walks the cascade node-by-node.
  // After the last node lands, normal auto-tour resumes after a short
  // settle delay.
  const [liveTriggerId, setLiveTriggerId] = useState(null);   // chain id currently in live mode
  const [liveNodeIdx, setLiveNodeIdx] = useState(-1);          // -1 = none lit; 0..N-1 = current step

  const intervalRef = useRef(null);
  const liveTimerRef = useRef(null);
  const TOUR_INTERVAL_MS = 8000;

  const active = CHAINS.find((c) => c.id === activeId) || CHAINS[0];
  const isLive = liveTriggerId === activeId && liveNodeIdx >= 0;

  // Listen for real submissions across the app. When fired, jump to
  // the matching chain, suspend auto-tour, and walk node-by-node.
  useEffect(() => {
    const onSubmit = (ev) => {
      const kind = (ev?.detail?.kind || "").toLowerCase();
      const chainId = SUBMISSION_TO_CHAIN[kind];
      if (!chainId) return;
      const chain = CHAINS.find((c) => c.id === chainId);
      if (!chain) return;

      // Cancel any in-flight live walk
      if (liveTimerRef.current) {
        clearInterval(liveTimerRef.current);
        liveTimerRef.current = null;
      }
      // Switch to the matching chain and start the cascade
      setActiveId(chainId);
      setLiveTriggerId(chainId);
      setLiveNodeIdx(0);
      setTickKey((k) => k + 1);

      let step = 1;
      liveTimerRef.current = setInterval(() => {
        if (step >= chain.nodes.length) {
          // Last node has lit. Settle for ~2s, then release back to auto-tour.
          clearInterval(liveTimerRef.current);
          liveTimerRef.current = setTimeout(() => {
            setLiveTriggerId(null);
            setLiveNodeIdx(-1);
            setTickKey((k) => k + 1);
          }, 2200);
          return;
        }
        setLiveNodeIdx(step);
        step += 1;
      }, LIVE_NODE_MS);
    };

    window.addEventListener("forgeops:submission", onSubmit);
    return () => {
      window.removeEventListener("forgeops:submission", onSubmit);
      if (liveTimerRef.current) {
        clearInterval(liveTimerRef.current);
        clearTimeout(liveTimerRef.current);
        liveTimerRef.current = null;
      }
    };
  }, []);

  // Advance to the next chain on a fixed cadence while auto-tour is on
  // AND the panel is not hovered AND no live submission is currently
  // walking the cascade.
  useEffect(() => {
    if (!isAutoTour || isPaused || liveTriggerId) return undefined;
    intervalRef.current = setInterval(() => {
      setActiveId((cur) => {
        const idx = CHAINS.findIndex((c) => c.id === cur);
        const next = CHAINS[(idx + 1) % CHAINS.length];
        return next.id;
      });
      setTickKey((k) => k + 1);
    }, TOUR_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [isAutoTour, isPaused, activeId, liveTriggerId]);

  const onPickTab = (id) => {
    // Manual pick cancels any live walk
    if (liveTimerRef.current) {
      clearInterval(liveTimerRef.current);
      clearTimeout(liveTimerRef.current);
      liveTimerRef.current = null;
    }
    setLiveTriggerId(null);
    setLiveNodeIdx(-1);
    setActiveId(id);
    setTickKey((k) => k + 1);
  };

  const toggleAuto = () => {
    setIsAutoTour((v) => !v);
    setTickKey((k) => k + 1);
  };

  return (
    <section
      className="rounded-md overflow-hidden"
      style={{ background: "#0A0A0A", border: "1px solid #1A1A1A" }}
      data-testid="workflow-chain-panel"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Header */}
      <div
        className="px-5 sm:px-7 py-5 sm:py-6 border-b relative"
        style={{ borderColor: "#1A1A1A", background: "#0B0B0B" }}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div
              className="font-mono text-[10px] uppercase tracking-[0.3em] mb-2 flex items-center gap-2"
              style={{ color: "#FF6A00" }}
            >
              <Activity className="w-3.5 h-3.5" />
              Operational Routing · System Chain
            </div>
            <h3 className="font-display text-xl sm:text-2xl font-black tracking-tight text-white">
              One submission. The whole platform reacts.
            </h3>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={toggleAuto}
              disabled={!!liveTriggerId}
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1.5 rounded transition-colors disabled:opacity-90"
              style={{
                background: liveTriggerId
                  ? "rgba(255,106,0,0.14)"
                  : isAutoTour
                  ? "#1A1A1A"
                  : "#0F0F0F",
                color: liveTriggerId
                  ? "#FF6A00"
                  : isAutoTour
                  ? (isPaused ? "#FFB000" : "#65DA76")
                  : "#888",
                border: `1px solid ${
                  liveTriggerId
                    ? "rgba(255,106,0,0.55)"
                    : isAutoTour
                    ? (isPaused ? "#FFB000" : "#65DA76") + "55"
                    : "#2A2A2A"
                }`,
              }}
              data-testid="workflow-chain-autotour-toggle"
              aria-label={
                liveTriggerId
                  ? "Live submission routing"
                  : isAutoTour
                  ? "Pause auto-tour"
                  : "Start auto-tour"
              }
              title={
                liveTriggerId
                  ? "Real submission routing live"
                  : isAutoTour
                  ? "Auto-tour active · hover to pause"
                  : "Auto-tour paused"
              }
            >
              {liveTriggerId ? (
                <>
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{
                      background: "#FF6A00",
                      boxShadow: "0 0 6px rgba(255,106,0,0.85)",
                      animation: "fo-live-pulse 1.4s ease-in-out infinite",
                    }}
                  />
                  {t("Live · Submission Routing")}
                </>
              ) : isAutoTour ? (
                isPaused ? (
                  <>
                    <Pause className="w-3 h-3" /> {t("Auto-tour · Hover-paused")}
                  </>
                ) : (
                  <>
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: "#65DA76", boxShadow: "0 0 5px #65DA76" }}
                    />
                    {t("Auto-tour · Live")}
                  </>
                )
              ) : (
                <>
                  <Play className="w-3 h-3" /> {t("Start auto-tour")}
                </>
              )}
            </button>
            <span
              className="font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 rounded hidden sm:inline-flex items-center gap-1.5"
              style={{
                background: "#1A1A1A",
                color: "#65DA76",
                border: "1px solid #2A2A2A",
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#65DA76" }} /> Routing live
            </span>
          </div>
        </div>

        {/* Chain selector */}
        <div className="mt-5 flex flex-wrap gap-2" data-testid="workflow-chain-tabs">
          {CHAINS.map((c) => {
            const isActive = c.id === activeId;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => onPickTab(c.id)}
                className="font-mono text-[10px] uppercase tracking-[0.25em] font-bold h-9 px-4 rounded transition-colors"
                style={{
                  background: isActive ? "#FF6A00" : "#1A1A1A",
                  color: isActive ? "#000" : "#FFFFFF",
                  border: `1px solid ${isActive ? "#FF6A00" : "#2A2A2A"}`,
                }}
                data-testid={`workflow-chain-tab-${c.id}`}
              >
                {c.title}
              </button>
            );
          })}
        </div>

        {/* Auto-tour progress hairline at bottom edge of header — only
            visible when the auto-tour is actively cycling. Restrained:
            a 1px line that fills across the panel width over 8s, then
            resets when the next chain locks in. */}
        {isAutoTour && !isPaused && !liveTriggerId && (
          <div
            aria-hidden
            className="absolute left-0 right-0 bottom-0 h-px overflow-hidden"
            style={{ background: "#1A1A1A" }}
          >
            <div
              key={tickKey}
              className="h-full"
              style={{
                background: "linear-gradient(90deg, transparent 0%, #FF6A00 50%, transparent 100%)",
                width: "100%",
                transformOrigin: "left",
                animation: `fo-chain-progress ${TOUR_INTERVAL_MS}ms linear forwards`,
              }}
              data-testid="workflow-chain-progress-bar"
            />
          </div>
        )}
      </div>

      {/* Chain body */}
      <div className="px-5 sm:px-7 py-6 sm:py-8" key={active.id}>
        <div
          className="font-mono text-[10px] uppercase tracking-[0.3em] mb-1.5"
          style={{ color: "#FF6A00" }}
        >
          {active.eyebrow}
        </div>
        <p
          className="text-sm leading-relaxed mb-7 max-w-3xl"
          style={{ color: "#BDBDBD" }}
        >
          {active.summary}
        </p>

        {/* Nodes — restrained, connected, directional */}
        <div
          className="grid grid-cols-1 lg:grid-cols-9 gap-3 lg:gap-0 items-stretch"
        >
          {active.nodes.map((node, i) => {
            const Icon = node.icon;
            const tone = TONE[node.tone] || TONE.info;
            const isLast = i === active.nodes.length - 1;

            // Live-mode: only nodes up to the current live step are
            // "lit". Future nodes are rendered dim until the cascade
            // reaches them.
            const liveLit  = isLive && i <= liveNodeIdx;
            const liveCurr = isLive && i === liveNodeIdx;
            const liveDim  = isLive && i > liveNodeIdx;

            const cardStyle = isLive
              ? {
                  background: liveLit ? "#0F0F0F" : "#080808",
                  border: `1px solid ${liveCurr ? tone.color : liveLit ? "#1A1A1A" : "#101010"}`,
                  boxShadow: liveCurr ? `inset 0 0 0 1px ${tone.color}55` : "none",
                  opacity: liveDim ? 0.45 : 1,
                  transition: "opacity 320ms ease, border-color 320ms ease, box-shadow 320ms ease, background 320ms ease",
                  minHeight: 124,
                }
              : {
                  background: "#0F0F0F",
                  border: "1px solid #1A1A1A",
                  minHeight: 124,
                  animation: `fo-chain-fade 0.4s ease-out ${i * 0.08}s both`,
                };

            return (
              <React.Fragment key={i}>
                <div
                  className="lg:col-span-1 p-3 rounded-md flex flex-col items-start gap-2 relative"
                  style={cardStyle}
                  data-testid={`workflow-chain-node-${i}`}
                  data-live-state={liveCurr ? "current" : liveLit ? "lit" : liveDim ? "dim" : "static"}
                >
                  <span
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md shrink-0"
                    style={{
                      background: `${tone.color}1F`,
                      color: tone.color,
                      border: `1px solid ${tone.color}55`,
                      transition: "transform 320ms ease",
                      transform: liveCurr ? "scale(1.04)" : "scale(1)",
                    }}
                  >
                    <Icon className="w-4 h-4" />
                  </span>
                  <div className="flex-1">
                    <div
                      className="font-mono text-[9px] uppercase tracking-[0.25em] font-bold mb-1"
                      style={{ color: liveCurr ? tone.color : "#666" }}
                    >
                      Step {String(i + 1).padStart(2, "0")} · {node.actor}
                    </div>
                    <div className="text-[12px] font-bold leading-snug text-white">
                      {node.action}
                    </div>
                    <div
                      className="font-mono text-[9px] mt-1.5 leading-snug"
                      style={{ color: "#8A8A8A" }}
                    >
                      {node.meta}
                    </div>
                  </div>
                </div>
                {!isLast && (
                  <div
                    className="hidden lg:flex items-center justify-center"
                    aria-hidden
                  >
                    <ArrowRight
                      className="w-4 h-4"
                      style={{
                        color: "#FF6A00",
                        opacity: isLive
                          ? (i < liveNodeIdx ? 0.95 : i === liveNodeIdx ? 0.6 : 0.18)
                          : 0.5,
                        transition: "opacity 320ms ease",
                      }}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Outcome chip */}
        <div
          className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1.5 rounded transition-colors"
          style={
            isLive
              ? {
                  background: "rgba(255,106,0,0.10)",
                  color: "#FF6A00",
                  border: "1px solid rgba(255,106,0,0.4)",
                }
              : {
                  background: "rgba(101,218,118,0.08)",
                  color: "#65DA76",
                  border: "1px solid rgba(101,218,118,0.4)",
                }
          }
          data-testid="workflow-chain-outcome"
        >
          {isLive ? (
            <>
              <span
                className="w-2 h-2 rounded-full"
                style={{
                  background: "#FF6A00",
                  boxShadow: "0 0 6px rgba(255,106,0,0.8)",
                  animation: "fo-live-pulse 1.4s ease-in-out infinite",
                }}
              />
              Submission active · Step {liveNodeIdx + 1} of {active.nodes.length} · Routing through infrastructure
            </>
          ) : (
            <>
              <CheckCircle2 className="w-3.5 h-3.5" />
              Auto-routed · Audit-trail logged · Same-day visibility
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fo-chain-fade {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fo-chain-progress {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
        @keyframes fo-live-pulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.4; }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="workflow-chain-progress-bar"] { animation: none !important; }
          [data-testid^="workflow-chain-node-"]       { animation: none !important; }
        }
      `}</style>
    </section>
  );
}
